import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import ShopNav from "@/components/feature/ShopNav";
import ShopFooter from "@/components/feature/ShopFooter";
import {
  supabase,
  getPrimaryImage,
  getEffectivePrice,
  formatPrice,
  type Product,
} from "@/lib/supabase";
import { useCart } from "@/hooks/useCart";

const TEXT_COLOR = "#545454";
const BORDER_SOFT = "rgba(84,84,84,0.14)";
const BORDER_MID = "rgba(84,84,84,0.22)";
const CARD_BG = "#F6F4EF";

type Tab = "story" | "care" | "shipping";

export default function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState<Product | null>(null);
  const [family, setFamily] = useState<Product[]>([]);
  const [related, setRelated] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [qty, setQty] = useState(1);
  const [showAdded, setShowAdded] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>("story");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        setQty(1);
        setActiveTab("story");
        const pid = Number(id);
        if (!pid) throw new Error("Invalid product id");

        const { data, error: e } = await supabase
          .from("product_items")
          .select("*, product_categories(id, name, sort_order)")
          .eq("id", pid)
          .maybeSingle();
        if (cancelled) return;
        if (e) throw e;
        if (!data) throw new Error("Product not found");
        setProduct(data as Product);

        // Fetch knitted family (all 6 crochet plush)
        const famRes = await supabase
          .from("product_items")
          .select("*, product_categories(id, name, sort_order)")
          .eq("status", "active")
          .in("id", [100006, 100007, 100008, 100009, 100010, 100011])
          .order("id", { ascending: true });
        if (!cancelled && !famRes.error) {
          setFamily((famRes.data ?? []) as Product[]);
        }

        // Related — same category, excluding current
        const catId = (data as Product).product_categories?.id;
        if (catId) {
          const relRes = await supabase
            .from("product_items")
            .select("*, product_categories(id, name, sort_order)")
            .eq("status", "active")
            .eq("category_id", catId)
            .neq("id", pid)
            .limit(4);
          if (!cancelled && !relRes.error) {
            setRelated((relRes.data ?? []) as Product[]);
          }
        }
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : "Failed to load product";
        if (!cancelled) setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleAdd = () => {
    if (!product) return;
    addToCart(
      {
        productId: product.id,
        name: product.name,
        image: getPrimaryImage(product),
        unitPrice: getEffectivePrice(product),
        currency: product.currency,
        stock: product.stock ?? 999,
      },
      qty,
    );
    setShowAdded(true);
    setTimeout(() => setShowAdded(false), 2200);
  };

  const handleBuyNow = () => {
    if (!product) return;
    addToCart(
      {
        productId: product.id,
        name: product.name,
        image: getPrimaryImage(product),
        unitPrice: getEffectivePrice(product),
        currency: product.currency,
        stock: product.stock ?? 999,
      },
      qty,
    );
    navigate("/checkout");
  };

  if (loading) {
    return (
      <main style={{ background: "#FFFFFF", minHeight: "100vh" }}>
        <ShopNav variant="light" />
        <div
          style={{
            padding: "200px 32px",
            textAlign: "center",
            color: "rgba(84,84,84,0.55)",
            fontSize: 12,
            letterSpacing: "2px",
          }}
        >
          LOADING…
        </div>
      </main>
    );
  }

  if (error || !product) {
    return (
      <main style={{ background: "#FFFFFF", minHeight: "100vh" }}>
        <ShopNav variant="light" />
        <div style={{ padding: "200px 32px", textAlign: "center" }}>
          <p style={{ color: "#B14837", fontSize: 14 }}>{error || "Product not found"}</p>
          <Link
            to="/catalog"
            style={{
              display: "inline-block",
              marginTop: 16,
              color: TEXT_COLOR,
              fontSize: 12,
              letterSpacing: "2px",
              textDecoration: "underline",
            }}
          >
            BACK TO CATALOG
          </Link>
        </div>
        <ShopFooter />
      </main>
    );
  }

  const price = getEffectivePrice(product);
  const img = getPrimaryImage(product);
  const inStock = (product.stock ?? 0) > 0;
  const displayName = product.name.split("—")[0]?.trim() || product.name;
  const subtitle = product.name.split("—")[1]?.trim();
  const categoryName = product.product_categories?.name ?? "Toys";
  const numInFamily = family.findIndex((f) => f.id === product.id);
  const num = String(numInFamily >= 0 ? numInFamily + 1 : 1).padStart(2, "0");

  return (
    <main
      style={{
        background: "#FFFFFF",
        minHeight: "100vh",
        fontFamily: "'Inter Tight', sans-serif",
        color: TEXT_COLOR,
      }}
    >
      <ShopNav variant="light" />

      {/* Breadcrumb */}
      <section style={{ padding: "104px 40px 12px", maxWidth: 1280, margin: "0 auto" }}>
        <div
          style={{
            fontSize: 10,
            color: "rgba(84,84,84,0.55)",
            letterSpacing: "1.8px",
          }}
        >
          <Link
            to="/"
            className="cursor-pointer hover:opacity-60"
            style={{ color: "inherit", textDecoration: "none" }}
          >
            HOME
          </Link>
          <span style={{ margin: "0 8px" }}>/</span>
          <Link
            to="/catalog"
            className="cursor-pointer hover:opacity-60"
            style={{ color: "inherit", textDecoration: "none" }}
          >
            CATALOG
          </Link>
          <span style={{ margin: "0 8px" }}>/</span>
          <span style={{ color: TEXT_COLOR }}>{displayName.toUpperCase()}</span>
        </div>
      </section>

      {/* MAIN PRODUCT SECTION */}
      <section style={{ padding: "16px 40px 40px", maxWidth: 1280, margin: "0 auto" }}>
        <div
          className="grid"
          style={{
            gridTemplateColumns: "1fr 1fr",
            gap: 56,
            alignItems: "start",
          }}
        >
          {/* Left — image */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            style={{ position: "relative" }}
          >
            <div
              style={{
                position: "relative",
                width: "100%",
                aspectRatio: "1 / 1",
                background: CARD_BG,
                borderRadius: 4,
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 32,
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: "12%",
                  background:
                    "radial-gradient(circle, rgba(255,255,255,0.75), rgba(255,255,255,0) 70%)",
                  pointerEvents: "none",
                }}
              />
              <motion.img
                key={img}
                src={img}
                alt={product.name}
                initial={{ scale: 0.92, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  position: "relative",
                  zIndex: 1,
                }}
              />

              {/* handmade stamp */}
              <div
                style={{
                  position: "absolute",
                  bottom: 20,
                  right: 22,
                  zIndex: 2,
                  fontFamily: "'Instrument Serif', serif",
                  fontStyle: "italic",
                  fontSize: 22,
                  color: "rgba(84,84,84,0.55)",
                  transform: "rotate(-6deg)",
                  letterSpacing: "-0.5px",
                }}
              >
                handmade
              </div>

              {/* Corner tag */}
              <div
                style={{
                  position: "absolute",
                  top: 16,
                  left: 16,
                  zIndex: 2,
                  fontSize: 9,
                  letterSpacing: "1.8px",
                  color: "rgba(84,84,84,0.55)",
                  textTransform: "uppercase",
                }}
              >
                ({num}) · {categoryName} · Milano
              </div>
            </div>

            {/* Under-image editorial captions */}
            <div
              className="flex flex-wrap"
              style={{
                gap: 6,
                marginTop: 14,
                paddingTop: 14,
                borderTop: `1px solid ${BORDER_SOFT}`,
              }}
            >
              {["100% Cotton", "Milano atelier", "Ships in 48h"].map((b) => (
                <div
                  key={b}
                  style={{
                    padding: "5px 11px",
                    border: `1px solid ${BORDER_MID}`,
                    borderRadius: 999,
                    fontSize: 9,
                    color: "rgba(84,84,84,0.75)",
                    letterSpacing: "1.5px",
                    background: "#FFFFFF",
                  }}
                >
                  {b.toUpperCase()}
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right — info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut", delay: 0.15 }}
          >
            <div
              style={{
                fontSize: 10,
                letterSpacing: "2px",
                color: "rgba(84,84,84,0.55)",
                marginBottom: 10,
                textTransform: "uppercase",
              }}
            >
              ({num}) · {categoryName}
            </div>
            <h1
              style={{
                fontFamily: "'Instrument Serif', serif",
                fontStyle: "italic",
                fontWeight: 400,
                fontSize: 60,
                lineHeight: 0.95,
                letterSpacing: "-2px",
                color: TEXT_COLOR,
                margin: 0,
              }}
            >
              {displayName}
            </h1>
            {subtitle && (
              <div
                style={{
                  fontSize: 14,
                  color: "rgba(84,84,84,0.7)",
                  marginTop: 12,
                  letterSpacing: "0.2px",
                  lineHeight: 1.55,
                  maxWidth: 420,
                }}
              >
                {subtitle}
              </div>
            )}

            <div
              className="flex items-baseline"
              style={{ gap: 14, marginTop: 22 }}
            >
              <div
                style={{
                  fontFamily: "'Instrument Serif', serif",
                  fontStyle: "italic",
                  fontSize: 32,
                  fontWeight: 400,
                  color: TEXT_COLOR,
                  letterSpacing: "-0.8px",
                  lineHeight: 1,
                }}
              >
                {formatPrice(price, product.currency)}
              </div>
              <span
                style={{
                  fontSize: 9,
                  color: inStock ? "#5b8062" : "#B14837",
                  letterSpacing: "1.8px",
                  padding: "4px 10px",
                  border: `1px solid ${inStock ? "rgba(91,128,98,0.35)" : "rgba(177,72,55,0.35)"}`,
                  borderRadius: 999,
                }}
              >
                {inStock ? `${product.stock} IN STOCK` : "SOLD OUT"}
              </span>
            </div>

            {/* Tabs */}
            <div
              style={{
                marginTop: 26,
                borderTop: `1px solid ${BORDER_SOFT}`,
                paddingTop: 18,
              }}
            >
              <div className="flex" style={{ gap: 26 }}>
                {(
                  [
                    { key: "story", label: "The story" },
                    { key: "care", label: "Care" },
                    { key: "shipping", label: "Shipping" },
                  ] as { key: Tab; label: string }[]
                ).map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setActiveTab(t.key)}
                    className="cursor-pointer whitespace-nowrap"
                    style={{
                      background: "transparent",
                      border: 0,
                      padding: "0 0 8px",
                      fontSize: 10,
                      letterSpacing: "1.8px",
                      color: activeTab === t.key ? TEXT_COLOR : "rgba(84,84,84,0.5)",
                      borderBottom: `1.5px solid ${activeTab === t.key ? TEXT_COLOR : "transparent"}`,
                      transition: "all 0.2s ease",
                      textTransform: "uppercase",
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <div style={{ marginTop: 16, minHeight: 100 }}>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.25 }}
                    style={{
                      fontSize: 13,
                      lineHeight: 1.7,
                      color: "rgba(84,84,84,0.8)",
                    }}
                  >
                    {activeTab === "story" && <p style={{ margin: 0 }}>{product.description}</p>}
                    {activeTab === "care" && (
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        <li>Machine washable in a laundry bag at 30°C.</li>
                        <li>Air dry flat, away from direct sunlight.</li>
                        <li>Gentle brush to fluff up the knit after wash.</li>
                        <li>Naturally dyed cotton — colors may soften over time.</li>
                      </ul>
                    )}
                    {activeTab === "shipping" && (
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        <li>Free shipping across the EU on orders over €80.</li>
                        <li>Ships from Milano within 48 hours, wrapped by hand.</li>
                        <li>Every order includes a signed maker's card.</li>
                        <li>30-day gentle returns · Lifetime repair guarantee.</li>
                      </ul>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            {/* Quantity + Actions */}
            <div
              style={{
                marginTop: 8,
                paddingTop: 18,
                borderTop: `1px solid ${BORDER_SOFT}`,
              }}
            >
              <div
                className="flex items-center justify-between flex-wrap"
                style={{ gap: 16 }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 9,
                      letterSpacing: "1.8px",
                      color: "rgba(84,84,84,0.55)",
                      marginBottom: 8,
                      textTransform: "uppercase",
                    }}
                  >
                    Quantity
                  </div>
                  <div
                    className="flex items-center"
                    style={{
                      border: `1px solid ${BORDER_MID}`,
                      borderRadius: 999,
                      padding: "3px 5px",
                      background: "#FFFFFF",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setQty((q) => Math.max(1, q - 1))}
                      className="cursor-pointer w-7 h-7 flex items-center justify-center whitespace-nowrap"
                      style={{ background: "transparent", border: 0, color: TEXT_COLOR }}
                      aria-label="decrease"
                    >
                      <i className="ri-subtract-line" style={{ fontSize: 13 }}></i>
                    </button>
                    <span
                      style={{
                        minWidth: 28,
                        textAlign: "center",
                        fontSize: 13,
                        fontWeight: 500,
                        color: TEXT_COLOR,
                      }}
                    >
                      {qty}
                    </span>
                    <button
                      type="button"
                      onClick={() => setQty((q) => Math.min(product.stock ?? 99, q + 1))}
                      className="cursor-pointer w-7 h-7 flex items-center justify-center whitespace-nowrap"
                      style={{ background: "transparent", border: 0, color: TEXT_COLOR }}
                      aria-label="increase"
                    >
                      <i className="ri-add-line" style={{ fontSize: 13 }}></i>
                    </button>
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div
                    style={{
                      fontSize: 9,
                      letterSpacing: "1.8px",
                      color: "rgba(84,84,84,0.55)",
                      marginBottom: 6,
                      textTransform: "uppercase",
                    }}
                  >
                    Subtotal
                  </div>
                  <div
                    style={{
                      fontFamily: "'Instrument Serif', serif",
                      fontStyle: "italic",
                      fontSize: 22,
                      color: TEXT_COLOR,
                      letterSpacing: "-0.6px",
                      lineHeight: 1,
                    }}
                  >
                    {formatPrice(price * qty, product.currency)}
                  </div>
                </div>
              </div>

              <div className="flex" style={{ gap: 10, marginTop: 18 }}>
                <button
                  type="button"
                  onClick={handleAdd}
                  disabled={!inStock}
                  className="cursor-pointer whitespace-nowrap transition-opacity hover:opacity-80"
                  style={{
                    flex: 1,
                    background: "transparent",
                    color: TEXT_COLOR,
                    border: `1px solid ${TEXT_COLOR}`,
                    borderRadius: 999,
                    padding: "12px 20px",
                    fontSize: 11,
                    fontWeight: 500,
                    letterSpacing: "2px",
                    opacity: inStock ? 1 : 0.4,
                  }}
                >
                  ADD TO CART
                </button>
                <button
                  type="button"
                  onClick={handleBuyNow}
                  disabled={!inStock}
                  className="cursor-pointer whitespace-nowrap transition-opacity hover:opacity-85 inline-flex items-center justify-center"
                  style={{
                    flex: 1,
                    background: TEXT_COLOR,
                    color: "#FFFFFF",
                    border: `1px solid ${TEXT_COLOR}`,
                    borderRadius: 999,
                    padding: "12px 20px",
                    fontSize: 11,
                    fontWeight: 500,
                    letterSpacing: "2px",
                    opacity: inStock ? 1 : 0.4,
                    gap: 8,
                  }}
                >
                  BUY NOW
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-arrow-right-line" style={{ fontSize: 12 }}></i>
                  </span>
                </button>
              </div>

              <AnimatePresence>
                {showAdded && (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    style={{
                      marginTop: 18,
                      padding: "12px 16px",
                      background: "#FFFFFF",
                      border: `1px solid ${BORDER_SOFT}`,
                      borderRadius: 4,
                      fontSize: 13,
                      color: TEXT_COLOR,
                    }}
                  >
                    <span
                      style={{
                        fontFamily: "'Instrument Serif', serif",
                        fontStyle: "italic",
                        fontSize: 15,
                      }}
                    >
                      Added
                    </span>{" "}
                    {qty} × {displayName} to your cart —{" "}
                    <Link
                      to="/cart"
                      style={{ textDecoration: "underline", color: TEXT_COLOR }}
                    >
                      view cart
                    </Link>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Feature strip */}
            <div
              style={{
                marginTop: 24,
                paddingTop: 18,
                borderTop: `1px solid ${BORDER_SOFT}`,
              }}
            >
              <div
                className="grid"
                style={{ gridTemplateColumns: "1fr 1fr", gap: 12 }}
              >
                {[
                  { icon: "ri-hand-heart-line", label: "Handmade with love" },
                  { icon: "ri-plant-line", label: "Organic materials" },
                  { icon: "ri-truck-line", label: "Free EU shipping €80+" },
                  { icon: "ri-shield-check-line", label: "Lifetime repair" },
                ].map((f) => (
                  <div key={f.label} className="flex items-center" style={{ gap: 10 }}>
                    <div
                      className="w-6 h-6 flex items-center justify-center"
                      style={{
                        border: `1px solid ${BORDER_MID}`,
                        borderRadius: 999,
                        color: TEXT_COLOR,
                        flexShrink: 0,
                      }}
                    >
                      <i className={f.icon} style={{ fontSize: 12 }}></i>
                    </div>
                    <span style={{ fontSize: 12, color: "rgba(84,84,84,0.75)" }}>
                      {f.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* KNITTED FAMILY SHOWCASE */}
      {family.length > 0 && (
        <section
          style={{
            padding: "40px 40px 30px",
            maxWidth: 1280,
            margin: "0 auto",
            borderTop: `1px solid ${BORDER_SOFT}`,
          }}
        >
          <div className="flex items-center" style={{ gap: 14, marginBottom: 18, marginTop: 40 }}>
            <span
              style={{
                fontFamily: "'Instrument Serif', serif",
                fontStyle: "italic",
                fontSize: 22,
                color: "rgba(84,84,84,0.55)",
              }}
            >
              (α)
            </span>
            <span style={{ height: 1, flex: "0 0 60px", background: BORDER_MID }} />
            <span
              style={{
                fontSize: 11,
                letterSpacing: "2.5px",
                color: "rgba(84,84,84,0.55)",
                textTransform: "uppercase",
              }}
            >
              The knitted collection · six souls
            </span>
          </div>

          <div
            className="flex items-end justify-between"
            style={{ marginBottom: 44, gap: 20, flexWrap: "wrap" }}
          >
            <div style={{ maxWidth: 620 }}>
              <h2
                style={{
                  fontFamily: "'Fredoka', 'Inter Tight', sans-serif",
                  fontWeight: 600,
                  fontSize: 48,
                  lineHeight: 1,
                  letterSpacing: "-1.2px",
                  color: TEXT_COLOR,
                  margin: 0,
                }}
              >
                Meet the{" "}
                <span
                  style={{
                    fontFamily: "'Instrument Serif', serif",
                    fontStyle: "italic",
                    fontWeight: 400,
                    letterSpacing: "-2px",
                  }}
                >
                  whole family.
                </span>
              </h2>
              <p
                style={{
                  fontSize: 14,
                  color: "rgba(84,84,84,0.75)",
                  marginTop: 18,
                  lineHeight: 1.75,
                }}
              >
                Six little companions, each crocheted by hand in our Milano atelier. They
                come from the same yarn baskets, but every stitch gives them a personality
                of their own.
              </p>
            </div>
            <Link
              to="/catalog"
              className="cursor-pointer whitespace-nowrap transition-opacity hover:opacity-70 inline-flex items-center"
              style={{
                color: TEXT_COLOR,
                fontSize: 11,
                letterSpacing: "2px",
                textDecoration: "none",
                borderBottom: `1px solid ${TEXT_COLOR}`,
                paddingBottom: 4,
                gap: 8,
              }}
            >
              SHOP ALL
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-line" style={{ fontSize: 12 }}></i>
              </span>
            </Link>
          </div>

          <div
            className="grid"
            style={{
              gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
              gap: 24,
            }}
          >
            {family.map((f, i) => {
              const fImg = getPrimaryImage(f);
              const fPrice = getEffectivePrice(f);
              const fName = f.name.split("—")[0]?.trim() || f.name;
              const fSub = f.name.split("—")[1]?.trim() ?? "";
              const isCurrent = f.id === product.id;
              const fNum = String(i + 1).padStart(2, "0");
              return (
                <motion.div
                  key={f.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: i * 0.06, ease: "easeOut" }}
                >
                  <Link
                    to={`/product/${f.id}`}
                    className="cursor-pointer block group"
                    style={{ textDecoration: "none", position: "relative" }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: -14,
                        left: 2,
                        zIndex: 3,
                        fontFamily: "'Instrument Serif', serif",
                        fontStyle: "italic",
                        fontSize: 18,
                        color: "rgba(84,84,84,0.5)",
                        pointerEvents: "none",
                      }}
                    >
                      ({fNum})
                    </div>
                    <div
                      style={{
                        width: "100%",
                        aspectRatio: "1 / 1",
                        background: isCurrent ? "#F0EDE6" : CARD_BG,
                        borderRadius: 4,
                        overflow: "hidden",
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 26,
                        border: isCurrent
                          ? `1.5px solid ${TEXT_COLOR}`
                          : "1.5px solid transparent",
                        transition: "all 0.35s ease",
                      }}
                    >
                      {isCurrent && (
                        <div
                          style={{
                            position: "absolute",
                            top: 12,
                            left: 12,
                            zIndex: 3,
                            padding: "5px 11px",
                            background: TEXT_COLOR,
                            color: "#FFFFFF",
                            borderRadius: 999,
                            fontSize: 9,
                            letterSpacing: "1.5px",
                          }}
                        >
                          VIEWING
                        </div>
                      )}
                      <img
                        src={fImg}
                        alt={f.name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          transition: "transform 0.5s ease",
                        }}
                        className="group-hover:scale-110"
                      />
                    </div>
                    <div
                      className="flex items-start justify-between"
                      style={{ marginTop: 14, gap: 10 }}
                    >
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                          style={{
                            fontFamily: "'Instrument Serif', serif",
                            fontStyle: "italic",
                            fontSize: 24,
                            color: TEXT_COLOR,
                            letterSpacing: "-0.5px",
                            lineHeight: 1.1,
                            fontWeight: 400,
                          }}
                        >
                          {fName}
                        </div>
                        {fSub && (
                          <div
                            style={{
                              fontSize: 10,
                              color: "rgba(84,84,84,0.55)",
                              marginTop: 6,
                              letterSpacing: "1.5px",
                              textTransform: "uppercase",
                            }}
                          >
                            {fSub}
                          </div>
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: 13,
                          color: TEXT_COLOR,
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                          paddingTop: 4,
                        }}
                      >
                        {formatPrice(fPrice, f.currency)}
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </section>
      )}

      {/* CRAFT STORY STRIP */}
      <section
        style={{
          padding: "50px 40px 40px",
          maxWidth: 1280,
          margin: "0 auto",
        }}
      >
        <div className="flex items-center" style={{ gap: 14, marginBottom: 30 }}>
          <span
            style={{
              fontFamily: "'Instrument Serif', serif",
              fontStyle: "italic",
              fontSize: 22,
              color: "rgba(84,84,84,0.55)",
            }}
          >
            (β)
          </span>
          <span style={{ height: 1, flex: "0 0 60px", background: BORDER_MID }} />
          <span
            style={{
              fontSize: 11,
              letterSpacing: "2.5px",
              color: "rgba(84,84,84,0.55)",
              textTransform: "uppercase",
            }}
          >
            How each piece is born
          </span>
        </div>

        <div
          className="grid"
          style={{
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 0,
            border: `1px solid ${BORDER_SOFT}`,
            borderRadius: 4,
            overflow: "hidden",
          }}
        >
          {[
            {
              n: "01",
              title: "Hand-wound yarn",
              body: "Every ball of yarn is inspected and hand-wound before it reaches our maker's needles.",
            },
            {
              n: "02",
              title: "Stitched with time",
              body: "Each companion takes 10–14 hours to complete. No shortcuts, no machines, no rush.",
            },
            {
              n: "03",
              title: "Wrapped by hand",
              body: "Tucked into a linen dust pouch with a signed maker's card — ready to be loved.",
            },
          ].map((s, idx) => (
            <div
              key={s.n}
              style={{
                background: "#FFFFFF",
                padding: "34px 32px",
                borderRight: idx < 2 ? `1px solid ${BORDER_SOFT}` : "none",
              }}
            >
              <div
                style={{
                  fontFamily: "'Instrument Serif', serif",
                  fontStyle: "italic",
                  fontSize: 44,
                  color: "rgba(84,84,84,0.4)",
                  lineHeight: 1,
                  fontWeight: 400,
                }}
              >
                {s.n}
              </div>
              <div
                style={{
                  fontFamily: "'Instrument Serif', serif",
                  fontStyle: "italic",
                  fontSize: 28,
                  color: TEXT_COLOR,
                  marginTop: 14,
                  letterSpacing: "-0.6px",
                  fontWeight: 400,
                  lineHeight: 1.1,
                }}
              >
                {s.title}
              </div>
              <p
                style={{
                  fontSize: 13,
                  color: "rgba(84,84,84,0.75)",
                  marginTop: 12,
                  lineHeight: 1.75,
                }}
              >
                {s.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* RELATED PRODUCTS */}
      {related.length > 0 && (
        <section
          style={{
            padding: "16px 40px 60px",
            maxWidth: 1280,
            margin: "0 auto",
          }}
        >
          <div className="flex items-baseline" style={{ gap: 14, marginBottom: 30 }}>
            <span
              style={{
                fontFamily: "'Instrument Serif', serif",
                fontStyle: "italic",
                fontSize: 22,
                color: "rgba(84,84,84,0.55)",
              }}
            >
              (γ)
            </span>
            <h3
              style={{
                fontFamily: "'Instrument Serif', serif",
                fontStyle: "italic",
                fontSize: 40,
                color: TEXT_COLOR,
                margin: 0,
                letterSpacing: "-1px",
                fontWeight: 400,
                lineHeight: 1,
              }}
            >
              You may also like
            </h3>
          </div>
          <div
            className="grid"
            style={{
              gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
              gap: 32,
            }}
          >
            {related.map((r) => {
              const rImg = getPrimaryImage(r);
              const rPrice = getEffectivePrice(r);
              const rName = r.name.split("—")[0]?.trim() || r.name;
              return (
                <Link
                  key={r.id}
                  to={`/product/${r.id}`}
                  className="cursor-pointer block group"
                  style={{ textDecoration: "none" }}
                >
                  <div
                    style={{
                      width: "100%",
                      aspectRatio: "3 / 4",
                      background: CARD_BG,
                      overflow: "hidden",
                      borderRadius: 4,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: 24,
                    }}
                  >
                    <img
                      src={rImg}
                      alt={r.name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        transition: "transform 0.6s ease",
                      }}
                      className="group-hover:scale-105"
                    />
                  </div>
                  <div
                    className="flex items-start justify-between"
                    style={{ marginTop: 14, gap: 10 }}
                  >
                    <div
                      style={{
                        fontFamily: "'Instrument Serif', serif",
                        fontStyle: "italic",
                        fontSize: 22,
                        color: TEXT_COLOR,
                        letterSpacing: "-0.5px",
                        fontWeight: 400,
                        lineHeight: 1.1,
                      }}
                    >
                      {rName}
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        color: TEXT_COLOR,
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                        paddingTop: 2,
                      }}
                    >
                      {formatPrice(rPrice, r.currency)}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Editorial signature */}
      <div
        style={{
          maxWidth: 1280,
          margin: "0 auto",
          padding: "0 40px 40px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          borderTop: `1px solid ${BORDER_SOFT}`,
          paddingTop: 28,
        }}
      >
        <span
          style={{
            fontSize: 11,
            letterSpacing: "2.5px",
            color: "rgba(84,84,84,0.5)",
          }}
        >
          MAISON DES JOUETS · ATELIER
        </span>
        <span
          style={{
            fontFamily: "'Instrument Serif', serif",
            fontStyle: "italic",
            fontSize: 22,
            color: "rgba(84,84,84,0.5)",
          }}
        >
          — made slowly, with love
        </span>
      </div>

      <ShopFooter />
    </main>
  );
}